import { useEffect, useMemo, useRef, useState } from 'react';
import {
  atomsToTokens,
  calculateBaseMarginalPrice,
  calculateBoostedMarginalPrice,
  calculateVisibleRange,
  generateBaseCurvePoints,
  generateBoostedCurvePoints,
  getBandTransitions,
  lamportsToSol,
} from '../solana/curve/curveBands';
import './LiveRoundCurveChart.css';

/**
 * LiveRoundCurveChart - Visualizes the SolFire pricing curve
 * 
 * Shows:
 * - Base curve (without boost)
 * - Active curve (with boost)
 * - Current position marker
 * - Band transition guides
 * - Chart metadata overlay
 */
export function LiveRoundCurveChart({
  currentGameState,
  currentReserveVault,
  dumpVault,
  globalConfig,
}) {
  const svgRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 370 });
  const previousSupplyRef = useRef(null);
  const previousReserveLamportsRef = useRef(null);
  const [tradePopups, setTradePopups] = useState([]);
  const popupIdCounterRef = useRef(0);
  
  // Extract live data from game state
  const currentSupplyAtoms = currentGameState?.current_supply || '0';
  const boostLamports = Number(currentGameState?.boost || 0);
  const totalDumpedLamports = Number(currentGameState?.total_dumped_lamports || 0);
  const gameStatus = currentGameState?.status_label || 'pending';
  const gameId = globalConfig?.current_game_id || 'N/A';
  
  // Convert supply to whole tokens
  const currentSupplyTokens = atomsToTokens(currentSupplyAtoms);
  
  // Calculate visible range
  const { minTokens, maxTokens } = useMemo(
    () => calculateVisibleRange(currentSupplyTokens),
    [currentSupplyTokens]
  );
  
  // Generate curve points
  const baseCurvePoints = useMemo(
    () => generateBaseCurvePoints(minTokens, maxTokens, 150),
    [minTokens, maxTokens]
  );
  
  const boostedCurvePoints = useMemo(
    () => generateBoostedCurvePoints(minTokens, maxTokens, boostLamports, 150),
    [minTokens, maxTokens, boostLamports]
  );
  
  // Calculate current price
  const currentBasePrice = useMemo(
    () => calculateBaseMarginalPrice(currentSupplyTokens),
    [currentSupplyTokens]
  );
  
  const currentBoostedPrice = useMemo(
    () => calculateBoostedMarginalPrice(currentSupplyTokens, boostLamports),
    [currentSupplyTokens, boostLamports]
  );
  
  // Get band transitions
  const bandTransitions = useMemo(() => getBandTransitions(), []);
  
  // Calculate price range for Y axis
  const { minPrice, maxPrice } = useMemo(() => {
    const allPrices = [...baseCurvePoints, ...boostedCurvePoints].map((p) => p.y);
    const min = Math.min(...allPrices);
    const max = Math.max(...allPrices);
    const padding = (max - min) * 0.1;
    return {
      minPrice: Math.max(0, min - padding),
      maxPrice: max + padding,
    };
  }, [baseCurvePoints, boostedCurvePoints]);
  
  // Handle responsive sizing
  useEffect(() => {
    const handleResize = () => {
      if (svgRef.current) {
        const containerWidth = svgRef.current.parentElement?.clientWidth || 800;
        setDimensions({
          width: containerWidth,
          height: Math.max(300, Math.min(370, containerWidth * 0.46)),
        });
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Track supply changes for animation
  useEffect(() => {
    previousSupplyRef.current = currentSupplyTokens;
  }, [currentSupplyTokens]);
  
  // Track reserve vault changes to detect trades
  useEffect(() => {
    const currentReserveLamports = currentReserveVault?.lamports;
    
    // Skip if no reserve data yet
    if (currentReserveLamports == null) {
      return;
    }
    
    // Initialize previous value on first run
    if (previousReserveLamportsRef.current === null) {
      previousReserveLamportsRef.current = currentReserveLamports;
      return;
    }
    
    // Calculate delta
    const delta = currentReserveLamports - previousReserveLamportsRef.current;
    
    // Only create popup if there's a meaningful change (> 0.001 SOL)
    if (Math.abs(delta) > 1_000_000) {
      const isBuy = delta > 0;
      const solAmount = lamportsToSol(Math.abs(delta));
      
      // Calculate position using current values
      const padding = { top: 30, right: 60, bottom: 60, left: 110 };
      const chartWidth = dimensions.width - padding.left - padding.right;
      const chartHeight = dimensions.height - padding.top - padding.bottom;
      
      const scaleX = (tokens) => {
        return padding.left + ((tokens - minTokens) / (maxTokens - minTokens)) * chartWidth;
      };
      
      const scaleY = (price) => {
        return padding.top + chartHeight - ((price - minPrice) / (maxPrice - minPrice)) * chartHeight;
      };
      
      const baseX = scaleX(currentSupplyTokens);
      const baseY = scaleY(currentBoostedPrice);
      
      // Create popup at current position
      const popupId = popupIdCounterRef.current++;
      
      // Slight stagger for overlapping popups
      const stagger = (popupId % 5) * 15;
      
      setTradePopups((prev) => [
        ...prev,
        {
          id: popupId,
          x: baseX + stagger,
          y: baseY,
          amount: solAmount,
          isBuy,
          timestamp: Date.now(),
        },
      ]);
      
      // Remove popup after animation completes (1.5s)
      setTimeout(() => {
        setTradePopups((prev) => prev.filter((p) => p.id !== popupId));
      }, 1500);
    }
    
    // Update previous value
    previousReserveLamportsRef.current = currentReserveLamports;
  }, [
    currentReserveVault?.lamports,
    currentSupplyTokens,
    currentBoostedPrice,
    dimensions.width,
    dimensions.height,
    minTokens,
    maxTokens,
    minPrice,
    maxPrice,
  ]);
  
  // Force re-render for animations
  const [, forceUpdate] = useState({});
  useEffect(() => {
    if (tradePopups.length === 0) return;
    
    let animationFrameId;
    const animate = () => {
      forceUpdate({});
      animationFrameId = requestAnimationFrame(animate);
    };
    
    animationFrameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameId);
  }, [tradePopups.length]);
  
  // Calculate animated popup values
  const getAnimatedPopup = (popup) => {
    const elapsed = Date.now() - popup.timestamp;
    const progress = Math.min(elapsed / 1500, 1);
    const yOffset = progress * 40;
    return {
      ...popup,
      animatedY: popup.y - yOffset,
      opacity: 1 - progress,
    };
  };
  
  // Scale functions for mapping data to SVG coordinates
  const padding = { top: 30, right: 60, bottom: 60, left: 110 };
  const chartWidth = dimensions.width - padding.left - padding.right;
  const chartHeight = dimensions.height - padding.top - padding.bottom;
  
  const scaleX = (tokens) => {
    return padding.left + ((tokens - minTokens) / (maxTokens - minTokens)) * chartWidth;
  };
  
  const scaleY = (price) => {
    return padding.top + chartHeight - ((price - minPrice) / (maxPrice - minPrice)) * chartHeight;
  };
  
  // Generate SVG path for a curve
  const generatePath = (points) => {
    if (points.length === 0) return '';
    
    let path = `M ${scaleX(points[0].x)} ${scaleY(points[0].y)}`;
    for (let i = 1; i < points.length; i++) {
      path += ` L ${scaleX(points[i].x)} ${scaleY(points[i].y)}`;
    }
    return path;
  };
  
  const baseCurvePath = generatePath(baseCurvePoints);
  const boostedCurvePath = generatePath(boostedCurvePoints);
  
  // Current position coordinates
  const currentX = scaleX(currentSupplyTokens);
  const currentY = scaleY(currentBoostedPrice);
  
  // Format numbers for display
  const formatPrice = (lamports) => {
    const sol = lamportsToSol(lamports);
    if (sol === 0) return '0.000';
    if (sol < 0.0001) return '< 0.0001';
    if (sol < 0.001) return sol.toFixed(5);
    if (sol < 0.01) return sol.toFixed(4);
    if (sol < 1) return sol.toFixed(3);
    if (sol < 10) return sol.toFixed(2);
    return sol.toFixed(1);
  };
  
  const formatSupply = (tokens) => {
    if (tokens < 1000) return tokens.toFixed(0);
    if (tokens < 10_000) return `${(tokens / 1000).toFixed(2)}k`;
    return `${(tokens / 1000).toFixed(1)}k`;
  };
  
  const formatBoost = (lamports) => {
    const sol = lamportsToSol(lamports);
    if (sol < 0.001) return `+${(lamports / 1000).toFixed(0)}k`;
    return `+${sol.toFixed(4)} SOL`;
  };
  
  const formatDumped = (lamports) => {
    const sol = lamportsToSol(lamports);
    return `${sol.toFixed(3)} SOL`;
  };
  
  return (
    <div className="live-curve-chart">
      <div className="live-curve-chart__header">
        <div className="live-curve-chart__meta-item">
          <span className="live-curve-chart__meta-label">Current Price</span>
          <span className="live-curve-chart__meta-value live-curve-chart__meta-value--price">
            {formatPrice(currentBoostedPrice)} SOL
          </span>
        </div>
        
        <div className="live-curve-chart__meta-item live-curve-chart__meta-item--center">
          <span className="live-curve-chart__meta-label">Platform Liquidity Injected</span>
          <span className="live-curve-chart__meta-value live-curve-chart__meta-value--liquidity">
            {lamportsToSol(totalDumpedLamports).toFixed(2)} SOL
          </span>
        </div>
        
        <div className="live-curve-chart__legend">
          <div className="live-curve-chart__legend-item">
            <span className="live-curve-chart__legend-line live-curve-chart__legend-line--base" />
            <span>Base Curve</span>
          </div>
          <div className="live-curve-chart__legend-item">
            <span className="live-curve-chart__legend-line live-curve-chart__legend-line--active" />
            <span>Live Curve</span>
          </div>
          <div className="live-curve-chart__legend-item">
            <span className="live-curve-chart__legend-marker" />
            <span>Current Position</span>
          </div>
        </div>
      </div>
      
      <div className="live-curve-chart__body">
        <svg
          ref={svgRef}
          className="live-curve-chart__svg"
          width={dimensions.width}
          height={dimensions.height}
          viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        >
          {/* Gradient definitions */}
          <defs>
            <linearGradient id="activeCurveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#ff9c40" />
              <stop offset="50%" stopColor="#ffc060" />
              <stop offset="100%" stopColor="#ff9c40" />
            </linearGradient>
          </defs>
            
            {/* Grid lines */}
            <g className="live-curve-chart__grid">
              {[0, 0.25, 0.5, 0.75, 1].map((ratio) => (
                <line
                  key={`grid-h-${ratio}`}
                  x1={padding.left}
                  y1={padding.top + chartHeight * ratio}
                  x2={padding.left + chartWidth}
                  y2={padding.top + chartHeight * ratio}
                  className="live-curve-chart__grid-line"
                />
              ))}
              {[0, 0.25, 0.5, 0.75, 1].map((ratio) => (
                <line
                  key={`grid-v-${ratio}`}
                  x1={padding.left + chartWidth * ratio}
                  y1={padding.top}
                  x2={padding.left + chartWidth * ratio}
                  y2={padding.top + chartHeight}
                  className="live-curve-chart__grid-line"
                />
              ))}
            </g>
            
            {/* Band transition guides */}
            {bandTransitions.map((transition) => {
              if (transition.tokens < minTokens || transition.tokens > maxTokens) return null;
              const x = scaleX(transition.tokens);
              return (
                <g key={`band-${transition.tokens}`}>
                  <line
                    x1={x}
                    y1={padding.top}
                    x2={x}
                    y2={padding.top + chartHeight}
                    className="live-curve-chart__band-line"
                  />
                  <text
                    x={x}
                    y={padding.top - 8}
                    className="live-curve-chart__band-label"
                    textAnchor="middle"
                  >
                    {transition.label}
                  </text>
                </g>
              );
            })}
            
            {/* Base curve */}
            <path
              d={baseCurvePath}
              className="live-curve-chart__curve live-curve-chart__curve--base"
              fill="none"
              strokeWidth="2"
            />
            
            {/* Boosted curve */}
            <path
              d={boostedCurvePath}
              className="live-curve-chart__curve live-curve-chart__curve--active"
              fill="none"
              strokeWidth="3"
            />
            
            {/* Current position marker */}
            <g className="live-curve-chart__position">
              <circle
                cx={currentX}
                cy={currentY}
                r="8"
                className="live-curve-chart__position-glow"
              />
              <circle
                cx={currentX}
                cy={currentY}
                r="5"
                className="live-curve-chart__position-dot"
              />
            </g>
            
            {/* Trade popups */}
            {tradePopups.map((popup) => {
              const animated = getAnimatedPopup(popup);
              return (
                <text
                  key={popup.id}
                  x={animated.x}
                  y={animated.animatedY}
                  className={`live-curve-chart__trade-popup ${
                    animated.isBuy ? 'live-curve-chart__trade-popup--buy' : 'live-curve-chart__trade-popup--sell'
                  }`}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  style={{ 
                    pointerEvents: 'none',
                    opacity: animated.opacity,
                  }}
                >
                  {animated.isBuy ? '+' : '-'}
                  {animated.amount.toFixed(animated.amount < 0.01 ? 4 : animated.amount < 1 ? 3 : 2)} SOL
                </text>
              );
            })}
            
            {/* Axes */}
            <g className="live-curve-chart__axis">
              {/* Y axis labels */}
              {[0, 0.5, 1].map((ratio) => {
                const price = minPrice + (maxPrice - minPrice) * (1 - ratio);
                const y = padding.top + chartHeight * ratio;
                return (
                  <text
                    key={`y-label-${ratio}`}
                    x={padding.left - 10}
                    y={y}
                    className="live-curve-chart__axis-label"
                    textAnchor="end"
                    dominantBaseline="middle"
                  >
                    {formatPrice(price)} SOL
                  </text>
                );
              })}
              
              {/* X axis labels */}
              {[0, 0.5, 1].map((ratio) => {
                const tokens = minTokens + (maxTokens - minTokens) * ratio;
                const x = padding.left + chartWidth * ratio;
                return (
                  <text
                    key={`x-label-${ratio}`}
                    x={x}
                    y={padding.top + chartHeight + 25}
                    className="live-curve-chart__axis-label"
                    textAnchor="middle"
                  >
                    {formatSupply(tokens)}
                  </text>
                );
              })}
              
              {/* Axis titles */}
              <text
                x={padding.left - 90}
                y={padding.top + chartHeight / 2}
                className="live-curve-chart__axis-title"
                textAnchor="middle"
                transform={`rotate(-90, ${padding.left - 90}, ${padding.top + chartHeight / 2})`}
              >
                Price (SOL)
              </text>
              <text
                x={padding.left + chartWidth / 2}
                y={padding.top + chartHeight + 50}
                className="live-curve-chart__axis-title"
                textAnchor="middle"
              >
                Token Supply
              </text>
            </g>
          </svg>
      </div>
    </div>
  );
}
